import { FC, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { DefaultGrid } from "../../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../../components/plugins";
import { Spinner } from "../../../../components/spiner/Spinner";
import { GosContractDataProvider } from "../../../../Services/DataProviders/GosContractDataProvider";
import useGridFilter from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { GosContactCommandsPanel } from "../components/GosContactCommandsPanel";
import styles from "../../../styles/index.module.scss";
import tabsStyles from "../../../../pages/styles/homePage.module.scss";
import classNames from "classnames";
import { useDetailsTabsPanel } from "../../../../system/hooks/useTabsPanel";
import { TabsPanel } from "../../../../components/tabs";
import { IInitialState, initialState, reducer } from "../state/GosContract/reducer";
import { usePluginContext } from "../../../../system/providers/plugin";
import { DatePeriodPanel } from "../../../../hoc/DatePeriodPanel/DatePeriodPanel";
import { IDatePeriod } from "../../../../components/datePeriodPicker/datePeriodPicker";
import SearchModalWindowAntd from "../../../../components/modalWindows/SearchModalWindowAntd/SearchModalWindowAntd";
import { DocumentType } from "../../../../@types/enumsGlobal";
import { GosContractSearchPanel } from "../components/GosContractSearchPanel";
import { DateTime } from "luxon";
import { IOption } from "../../../../components/selects/select";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { useTranslation } from "react-i18next";
import { Input, Space } from "antd";
import { DocumentGridStateType } from "../../../../@types/documents";
import { debounce } from "lodash";
import { SearchIcon } from "../../../../libs/corporate/icons/outlined/action/MainCollection";

const DefaultGridFilter = (datePeriod, isExecuteAmount?: boolean) => {

    let columnFilters = [] as IGridColumnFilter[]
    if (isExecuteAmount) {
        columnFilters.push({
            name: 'isExecuted',
            operator: 'Eq',
            value: 'false'
        })
    }

    return {
        numberPerPage: 15,
        pageNumber: 1,
        columnFilters: columnFilters,
        paramFilter:  { 
            startDate: datePeriod?.startDate?.toFormat("yyyyMMdd"),
            endDate: datePeriod?.endDate?.toFormat("yyyyMMdd")
        },        
    } as IGridFilter;
};

interface GosContractPluginViewProps {
    showSearchPanel?: boolean
    isExecuteAmount?: boolean
}

const MIN_SEARCH_LENGTH = 3

export const GosContractPluginView: FC<IGridProps & GosContractPluginViewProps> = (props) => {
    const appContext = useAppContext();
    const tabContext = useTabsContext();
    const pluginCtx = usePluginContext();

    const gosContractDP = new GosContractDataProvider(appContext.coreApiService);
    const [state, dispatch] = useReducer(reducer, initialState, (initialState) => {
        if (props.showSearchPanel) {
            return {
                ...initialState, DefaultDatePeriod: { startDate: DateTime.now().startOf('year'), endDate: DateTime.now() }
            }
        } else {
            return initialState
        }
    });
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultGridFilter(state.DefaultDatePeriod, props.isExecuteAmount));
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [modalWindow, setModalWindow] = useState(<></>);

    const gridData: any[] = props.data ? (props.data ?? []) : (state.data ?? []);
    const gridTotalCount = props.data ? (gridData.length === 0 ? 0 : (props.totalCount ?? 0)) : state.totalCount;

    const [searchValue, setSearchValue] = useState<string>('');
    const isRealRefresh = useRef(false);
    
    const { t } = useTranslation();

    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
    const [_, setViewState] = useState<DocumentGridStateType>("view");

    const handleSearchToggle = () => {
        if (state.selectedItem) {
            dispatch({ type: "changeViewStateHandler", payload: "search" });
        }
        setIsSearchModalVisible(!isSearchModalVisible);
        setViewState(isSearchModalVisible ? "return" : "search");
    };   

    const hasActiveFilters = () => {
        return !!gridFilter?.columnFilters?.length;
    };

    useEffect(() => {
        dispatch({ type: "changeSubmitting", payload: true });
        gosContractDP.getView(gridFilter, (data, totalCount) => {
            dispatch({ type: "getData", payload: { data: data, totalCount: totalCount } as IInitialState });
        });
    }, [gridFilter]);

    useEffect(() => {
        if (state.viewState == "refresh") {
            dispatch({ type: "changeSubmitting", payload: true });
            gosContractDP.getView(gridFilter, (data, totalCount) => {
                dispatch({
                    type: "getData",
                    payload: { data: data, totalCount: totalCount, viewState: "view" } as IInitialState,
                });
            });
        }
    }, [state.viewState]);
    
    function getData(options?: { searchOption: IOption, value: string }): void {
        pluginCtx.masterGrid.onUnselectEvent();
        pluginCtx.lots.dispatchGridFilter({ type: 'dropFilter' });
        dispatch({ type: "setSelectedItem", payload: undefined });
        dispatch({ type: "changeSubmitting", payload: true });

        if (options) {

            if (options?.searchOption.value === 'specName') {
                pluginCtx.lots.dispatchGridFilter({ type: 'paramFilter', payload: { gridParamFilter: { [options.searchOption.value]: options.value } } })

                dispatchGridFilter({ type: 'paramSimpleFilter', payload: { gridParamFilter: { [options.searchOption.value]: options.value }, gridColumnFilter: [] }})
            } else {
                    const filterSearchParam = [{
                        name: options.searchOption.value as string,
                        operator: 'Like' as OperatorType,
                        value: `%${options.value}%` as string
                    }]
                dispatchGridFilter({ type: 'paramSimpleFilter', payload: { gridParamFilter: {}, gridColumnFilter: filterSearchParam }})
            }

        } else {
            dispatchGridFilter({ type: 'dropFilter'})
        }
    }
   
    const searchContracts = useCallback((value: string) => {
        if (value.length >= MIN_SEARCH_LENGTH) {            
            const expression = `lower("Name") like lower('%${value}%') or lower("Code") like lower('%${value}%') ` +
                `or ("IdGlobal" in (select id_gos_contract_global from core.gos_contract_goods where lower(spec_name) like lower('%${value}%')))`;
            
            dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: expression });
        } else if (value.length === 0) {            
            dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
        }
    }, [dispatchGridFilter]);

    const debouncedSearch = useMemo(
        () => debounce(searchContracts, 1250),
        [searchContracts]
    );

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        if (props.isModal) {
            props.onFocusChanged?.(value.length > 0);
        }
        
        if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
            debouncedSearch(value);
        }
    }, [debouncedSearch, props.isModal, props.onFocusChanged]);

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);
    
    const handleRefresh = () => {
        isRealRefresh.current = true;
        dispatch({ type: "refreshData" });
    };

    return (
        <PluginWrapper>
            <ToolbarWrapper>
                <GridWrapper cols={3}>
                    {
                        props.showSearchPanel && (
                            <GosContractSearchPanel
                                // getView={getData}
                                dispatchGridFilter={dispatchGridFilter}
                                onFocusChanged={props.onFocusChanged}
                            />)
                    }
                    {
                        !props.showSearchPanel && (
                            <GridWrapper cols={2}>
                                <GosContactCommandsPanel
                                    add={{
                                        onClick: () => {
                                            tabContext.openChild("gos_contract_create_plugin", "create", undefined, () => {
                                                dispatch({ type: "refreshData" });
                                            });
                                        },
                                    }}
                                    delete={{
                                        onClick: () => {
                                            if (state.selectedItem)
                                                gosContractDP.markDelete(state.selectedItem.idGlobal, () => {
                                                    dispatch({ type: "refreshData" });
                                                });
                                        },
                                        disabled: !state.selectedItem || state.data?.find((x) => x.idGlobal == state.selectedItem?.idGlobal)?.deleted,
                                    }}
                                    restore={{
                                        onClick: () => {
                                            if (state.selectedItem)
                                                gosContractDP.restore(state.selectedItem.idGlobal, () => {
                                                    dispatch({ type: "refreshData" });
                                                });
                                        },
                                        disabled: !state.selectedItem || !state.data?.find((x) => x.idGlobal == state.selectedItem?.idGlobal)?.deleted,
                                    }}
                                    refresh={{ onClick: handleRefresh }}
                                    edit={{
                                        onClick: () => {
                                            if (state.selectedItem)
                                                tabContext.openChild("gos_contract_create_plugin", "edit", state.selectedItem.idGlobal, () => {
                                                    dispatch({ type: "refreshData" });
                                                });
                                        },
                                        disabled: !state.selectedItem,
                                    }}
                                    copy={{
                                        onClick: () => {
                                            if (state.selectedItem)
                                                tabContext.openChild("gos_contract_create_plugin", "copy", state.selectedItem.idGlobal, () => {
                                                    dispatch({ type: "refreshData" });
                                                });
                                        },
                                        disabled: !state.selectedItem,
                                    }}
                                    hasActiveFilters={hasActiveFilters()}
                                    search={{ 
                                        onClick: handleSearchToggle,
                                        // disabled: !state.selectedItem,
                                    }}                                   
                                    permission={props.plugin.permission}
                                />
                                <Input
                                    className="custom-input-withPrefix"
                                    style={{ width: 348, alignSelf: 'center', marginBottom: 0}}
                                    value={searchValue}
                                    placeholder={t("directory.gosContract.search")}
                                    prefix={<SearchIcon />}
                                    onPressEnter={() => {
                                        debouncedSearch.cancel();
                                        if (searchValue.length >= MIN_SEARCH_LENGTH || searchValue.length === 0) {
                                            searchContracts(searchValue);
                                        }
                                    }}
                                    onChange={handleSearchChange}
                                    onBlur={() => {
                                        if (props.isModal) props.onFocusChanged?.(false);
                                    }}
                                    allowClear
                                />
                                <Space></Space>
                            </GridWrapper>
                        )
                    }
                    <DatePeriodPanel
                        datePeriod={state.DefaultDatePeriod}
                        onDatePeriodChange={(date: IDatePeriod) => {
                            dispatch({ type: "changeDate", payload: date });
                            dispatchGridFilter({
                                type: 'paramSimpleFilter',
                                payload: {
                                    gridColumnFilter: gridFilter.columnFilters,
                                    gridParamFilter: { 
                                        startDate: date.startDate?.toFormat("yyyyMMdd"),
                                        endDate: date.endDate?.toFormat("yyyyMMdd")
                                    }
                                }
                            })
                        }}
                    />
                </GridWrapper>
            </ToolbarWrapper>
            {state.isSubmitting ? (
                <Spinner />
            ) : (
                <>
                    <DefaultGrid
                        autoSelect
                        showQuantity
                        separator
                        gridId={props.gridId}
                        data={gridData}
                        totalCount={gridTotalCount}
                        filter={gridFilter}
                        dataProvider={gosContractDP}
                        plugin={props.plugin}
                        selectedItem={state.selectedItem}
                        onDoubleClick={(row) => props.onDoubleClick?.(row)}
                        onSelect={(row) => {
                            dispatch({ type: "setSelectedItem", payload: row });
                            row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.gosContract) : pluginCtx.masterGrid.onUnselectEvent();
                            props.onSelect?.(row ? row : undefined);
                        }}
                        onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                        onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                        onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                        onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                    />
                        {isSearchModalVisible && (
                            <SearchModalWindowAntd
                                columns={props.plugin?.columns.filter((x) => x.visibility === true) ?? []}
                                defaultFilters={gridFilter.columnFilters}
                                search={(columnFilters) => {
                                    dispatchGridFilter({ type: 'search', payload: columnFilters });
                                    handleSearchToggle();
                                    setViewState("refresh");
                                }}
                                cancel={handleSearchToggle}
                                open={true}
                                onCancel={handleSearchToggle}
                            />
                        )}
                    {
                        <div className={styles.detailsTabView}>
                            {detailsTabsPanel.tabs.length > 0 && state.selectedItem && (
                                <>
                                    <div className={tabsStyles.tabsPanelWrapper}>
                                        <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                            <TabsPanel
                                                id="detailsTabsPanel"
                                                activeId={detailsTabsPanel.currentTab?.id}
                                                tabs={detailsTabsPanel.tabs}
                                                onActive={(id) => {
                                                    dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                                }}
                                            ></TabsPanel>
                                        </div>
                                    </div>
                                    <div className={tabsStyles.contentWrapper}>
                                        {detailsTabsPanel.tabs.map((item) => {
                                            return (
                                                <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                    {item.view.content}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    }
                </>
            )}
            {modalWindow}
        </PluginWrapper>
    );
};
